import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, Target, Dumbbell, Trophy, Users, Sparkles } from 'lucide-react';

const PricingCalculator = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const questions = [
    {
      id: 'goal',
      question: "What's your main fitness goal?",
      options: [
        { value: 'fitness', label: 'General Fitness', description: 'HIIT, strength training, cardio', icon: Dumbbell },
        { value: 'martial_arts', label: 'Martial Arts', description: 'Muay Thai, BJJ, MMA, Boxing', icon: Target },
        { value: 'both', label: 'Both!', description: 'Fitness + Martial Arts training', icon: Sparkles },
        { value: 'pro', label: 'Competitive Fighting', description: 'Pro-level training for competition', icon: Trophy },
      ],
    },
    {
      id: 'frequency',
      question: 'How often do you plan to train?',
      options: [
        { value: '2-3', label: '2-3 days/week', description: 'Casual but consistent' },
        { value: '4-5', label: '4-5 days/week', description: 'Serious commitment' },
        { value: '6-7', label: '6-7 days/week', description: 'Maximum dedication' },
      ],
    },
    {
      id: 'commitment',
      question: 'How long do you want to commit?',
      options: [
        { value: '1', label: '1 Month', description: 'Try it out' },
        { value: '3', label: '3 Months', description: 'Get into a routine' },
        { value: '6', label: '6 Months', description: 'Real transformation', recommended: true },
        { value: '12', label: '12 Months', description: 'Best value' },
      ],
    },
  ];

  const getRecommendation = () => {
    const { goal, frequency, commitment } = answers;
    
    // Determine plan
    let plan, reason;
    
    if (goal === 'pro') {
      if (frequency === '6-7' || frequency === '4-5') {
        plan = 'Pro Classes - 5 Days / Week';
        reason = 'For serious competitive fighters who want maximum training time';
      } else {
        plan = 'Pro Classes - 3 Days / Week';
        reason = 'Perfect for aspiring competitors balancing training with other commitments';
      }
    } else if (goal === 'fitness') {
      plan = 'Fitness Only';
      reason = 'Access to all fitness classes, gym, and ice baths - perfect for your goals';
    } else {
      plan = 'Cobra Thai Til I Die';
      reason = 'Unlimited access to both martial arts AND fitness - the complete package';
    }

    // Get pricing
    const prices = {
      'Fitness Only': { '1': 2950, '3': 7965, '6': 15045, '12': 26550 },
      'Cobra Thai Til I Die': { '1': 4130, '3': 11151, '6': 21063, '12': 37170 },
      'Pro Classes - 3 Days / Week': { '1': 5150, '3': 13500, '6': 25500, '12': 48000 },
      'Pro Classes - 5 Days / Week': { '1': 6500, '3': 18000, '6': 33000, '12': 60000 },
    };

    const price = prices[plan][commitment];
    const monthlyPrice = Math.round(price / parseInt(commitment));

    // Calculate savings for longer commitments
    let savings = 0;
    if (commitment !== '1') {
      const monthlyAt1Month = prices[plan]['1'];
      savings = (monthlyAt1Month * parseInt(commitment)) - price;
    }

    return {
      plan,
      reason,
      duration: commitment === '1' ? '1 Month' : `${commitment} Months`,
      price,
      monthlyPrice,
      savings,
      requiresApproval: plan.includes('Pro'),
    };
  };

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      setTimeout(() => setShowResult(true), 300);
    }
  };

  const goBack = () => {
    if (showResult) {
      setShowResult(false);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setShowResult(false);
  };

  const currentQuestion = questions[step];
  const recommendation = showResult ? getRecommendation() : null;

  return (
    <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8 max-w-2xl mx-auto" data-testid="pricing-calculator">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {questions.map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < step || showResult ? 'bg-red-600' : i === step && !showResult ? 'bg-red-600/50' : 'bg-zinc-700'
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {!showResult ? (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Question */}
            <h3 className="text-2xl font-bold text-white mb-6" data-testid="calculator-question">
              {currentQuestion.question}
            </h3>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option) => {
                const Icon = option.icon;
                return (
                  <motion.button
                    key={option.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(currentQuestion.id, option.value)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      answers[currentQuestion.id] === option.value
                        ? 'bg-red-600/20 border-red-600'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                    data-testid={`option-${option.value}`}
                  >
                    <div className="flex items-center gap-4">
                      {Icon && (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          answers[currentQuestion.id] === option.value ? 'bg-red-600' : 'bg-zinc-700'
                        }`}>
                          <Icon size={20} className="text-white" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{option.label}</span>
                          {option.recommended && (
                            <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded-full">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-zinc-400">{option.description}</p>
                      </div>
                      <ChevronRight size={20} className="text-zinc-500" />
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Back button */}
            {step > 0 && (
              <button
                onClick={goBack}
                className="flex items-center gap-2 text-zinc-400 hover:text-white mt-6 transition-colors"
              >
                <ChevronLeft size={18} />
                Back
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            data-testid="calculator-result"
          >
            {/* Recommendation */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-red-500" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Your Perfect Plan</h3>
              <p className="text-zinc-400">Based on your answers, we recommend:</p>
            </div>

            <div className="bg-zinc-800/50 rounded-xl p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-xl font-bold text-white">{recommendation.plan}</h4>
                  <p className="text-sm text-zinc-400">{recommendation.duration}</p>
                </div>
                {recommendation.requiresApproval && (
                  <span className="px-2 py-1 bg-amber-600/20 text-amber-400 text-xs rounded-full">
                    Approval Required
                  </span>
                )}
              </div>

              <p className="text-zinc-300 mb-4">{recommendation.reason}</p>

              <div className="flex items-end gap-2 mb-2">
                <span className="text-4xl font-bold text-red-500">₹{recommendation.price.toLocaleString('en-IN')}</span>
                <span className="text-zinc-400 mb-1">/ {recommendation.duration.toLowerCase()}</span>
              </div>

              <p className="text-sm text-zinc-400">
                That's just ₹{recommendation.monthlyPrice.toLocaleString('en-IN')}/month
              </p>

              {recommendation.savings > 0 && (
                <div className="mt-4 flex items-center gap-2 text-green-400">
                  <Check size={18} />
                  <span className="text-sm">You save ₹{recommendation.savings.toLocaleString('en-IN')} with this plan!</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`https://wa.me/919545783636?text=${encodeURIComponent(`Hi! I'm interested in the ${recommendation.plan} membership`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-6 rounded-lg text-center transition-colors"
                data-testid="whatsapp-cta"
              >
                Get Started on WhatsApp
              </a>
              <button
                onClick={restart}
                className="flex-1 border border-zinc-700 hover:border-zinc-600 text-white py-3 px-6 rounded-lg transition-colors"
              >
                Start Over
              </button>
            </div>


            <button
              onClick={goBack}
              className="flex items-center gap-2 text-zinc-400 hover:text-white mt-4 mx-auto transition-colors"
            >
              <ChevronLeft size={18} />
              Change my answer
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PricingCalculator;
